import { ControllerRenderProps, Select } from '@otp/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Actions, Subjects } from 'src/graphql/generated/graphql';

type ActionProps = {
  field: ControllerRenderProps<any, 'action'>;
  hasError: boolean;
  className?: string;
  disabled?: boolean;
  subject: Subjects;
};

export const subjectActions = new Map<Subjects, Actions[]>([
  [
    Subjects.Users,
    [Actions.Read, Actions.Write, Actions.Assign, Actions.Unassign],
  ],
  [
    Subjects.UserGroups,
    [Actions.Read, Actions.Write, Actions.Assign, Actions.Unassign],
  ],

  [Subjects.Roles, [Actions.Read, Actions.Write]],
  [Subjects.Permissions, [Actions.Read, Actions.Write]],
  [Subjects.Stores, [Actions.Read, Actions.Write, Actions.MarkPrimary]],
  [Subjects.Tools, [Actions.Read]],
  [Subjects.Impersonate, [Actions.Write]],
  [Subjects.Notifications, [Actions.Read, Actions.Write]],
  [
    Subjects.Issues,
    [
      Actions.Read,
      Actions.Write,
      Actions.Assign,
      Actions.ChangeStatus,
      Actions.Comment,
    ],
  ],
  [Subjects.Curriculums, [Actions.Read, Actions.Write]],
  [Subjects.Certificates, [Actions.Read, Actions.Write, Actions.Delete]],
  [Subjects.Queries, [Actions.Read, Actions.Write, Actions.Execute]],
  [Subjects.Triages, [Actions.Read, Actions.Execute]],
  [Subjects.Jobs, [Actions.Read, Actions.Write]],
  [Subjects.History, [Actions.Read]],
  [Subjects.Problems, [Actions.Read]],
  [Subjects.Favourites, [Actions.Read, Actions.Write, Actions.Delete]],
  [Subjects.Devices, [Actions.Read]],
  [Subjects.Schedules, [Actions.Read]],
  [Subjects.Questions, [Actions.Read, Actions.Write]],
  [Subjects.QuestionCategories, [Actions.Read]],
  [Subjects.Questionnaires, [Actions.Read, Actions.Write]],
  [Subjects.QuestionnaireResponses, [Actions.Read, Actions.Write]],
  [
    Subjects.QuestionnaireResponses,
    [Actions.Read, Actions.Write, Actions.Schedule],
  ],
]);

export const ActionField = ({
  field,
  hasError,
  className,
  disabled = false,
  subject,
}: ActionProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'permissions.form.fields.action-options',
  });

  const options =
    useMemo(
      () =>
        (subjectActions.has(subject)
          ? subjectActions.get(subject)
          : Object.values(Actions)
        )?.map((item) => {
          return { value: item, name: t(item.toLowerCase()) };
        }),
      [t, subject],
    ) || [];

  const defaultValue =
    subjectActions.has(subject) &&
    subjectActions.get(subject)?.includes(field.value)
      ? field.value
      : options.length > 0
      ? options[0].value
      : Actions.Read;

  return (
    <Select
      key={field.value}
      fullWidth={true}
      className={className}
      options={options}
      placeholder={t('placeholder')}
      defaultValue={defaultValue}
      error={hasError}
      {...field}
      value={undefined}
      ref={undefined}
      inputRef={field.ref}
      disabled={disabled}
    />
  );
};
